/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/button-has-type */
import { Box, H3 } from '@ouster/webviz-react-components';

import { useNavigate } from 'react-router-dom';

import styles from './UserOrgSwitch.module.css';
import { useStoreState, useStoreActions } from '../../reducers';

const OrgTag = ({
  orgName,
  id,
  curOrgId,
  handleSwitchAccount,
}: {
  orgName: string;
  id: number;
  curOrgId: number;
  handleSwitchAccount: (id: number) => void;
}) => {
  const isActive = id === curOrgId;

  return (
    <button
      className={`${styles.tag} ${isActive && styles.active}`}
      onClick={() => handleSwitchAccount(id)}
      data-testid={`user-org-switch-btn-${id}`}
    >
      <span className={styles.tagLabel}>
        {orgName}
        {isActive ? ' (current)' : ''}
      </span>
    </button>
  );
};

export default function UserOrgSwitch({
  closeUserMenu,
}: {
  closeUserMenu: () => void;
}) {
  const { curUserOrg, userOrgs } = useStoreState(
    (storeState) => storeState.user
  );

  const { setCurUserOrg } = useStoreActions((actions) => actions.user);
  const { openConfirmDialog } = useStoreActions(
    (storeState) => storeState.confirmDialog
  );

  const navigate = useNavigate();

  function handleSwitchAccount(id: number) {
    const uploadsActive = sessionStorage.getItem('uploadsActive');

    const doSwitch = () => {
      // Cancel uploads in other components via a window event
      try {
        window.dispatchEvent(new Event('cancelAllUploads'));
      } catch (e) {
        // ignore
      }

      setCurUserOrg(id);
      sessionStorage.setItem('orgChanged', 'true');
      sessionStorage.setItem('columnVisibilityChanged', 'true');

      // If user is in a drive visualization, redirect them to private drive list
      const currentPath = window.location.pathname;
      const driveVizRegex = /\/organization\/\d+\/drive\/\d+/;
      if (driveVizRegex.test(currentPath)) {
        navigate('/drive/private');
      }

      closeUserMenu();
    };

    if (uploadsActive) {
      // Show confirmation dialog explaining uploads will be cancelled
      openConfirmDialog({
        title: 'Active uploads detected',
        bullets: [
          'Switching organizations will cancel all active uploads in this tab.',
          'If you want to keep these uploads running, open a new tab and switch organizations there instead.',
        ],
        confirmLabel: 'Cancel uploads and switch',
        cancelLabel: 'Go back',
        isDestructive: true,
        onConfirm: () => {
          // user confirmed - cancel uploads and switch
          doSwitch();
          return Promise.resolve();
        },
      });
      return;
    }

    // No active uploads - proceed normally (same effect as confirmed path)
    setCurUserOrg(id);
    sessionStorage.setItem('orgChanged', 'true');
    sessionStorage.setItem('columnVisibilityChanged', 'true');

    // If user is in a drive visualization, redirect them to private drive list
    const currentPath = window.location.pathname;
    const driveVizRegex = /\/organization\/\d+\/drive\/\d+/;
    if (driveVizRegex.test(currentPath)) {
      navigate('/drive/private');
    }

    closeUserMenu();
  }

  if (!curUserOrg) return null;

  return (
    <Box
      paddingBlock="0.75rem"
      paddingInline="1.25rem"
      fontSize={14}
      data-testid="user-org-switch-root"
    >
      <H3 fontSize={14}>User organizations</H3>
      <Box mt={6} display="grid" rowGap={4} maxH="30vh" overflowY="auto">
        <OrgTag
          key={curUserOrg.id}
          orgName={curUserOrg.name}
          id={curUserOrg.id}
          curOrgId={curUserOrg.id}
          handleSwitchAccount={handleSwitchAccount}
        />
        {userOrgs
          .filter((org) => org.id !== curUserOrg.id)
          .map((org) => (
            <OrgTag
              key={org.id}
              orgName={
                org.is_provisioned ? `${org.name} (provisioning)` : org.name
              }
              id={org.id}
              curOrgId={curUserOrg.id}
              handleSwitchAccount={handleSwitchAccount}
            />
          ))}
      </Box>
    </Box>
  );
}
